
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StepType } from "../hooks/sleep-dialog-types";
import { isCurrentStepValid, getValidationMessage } from "../utils/sleepDialogUtils";
import { EnhancedSleepForm } from "../hooks/useSleepDialogForm";
import { useState } from "react";

interface SleepDialogFooterProps {
  currentStep: StepType;
  steps: StepType[];
  saving: boolean;
  form: EnhancedSleepForm;
  onBack: () => void;
  onNext: () => void;
  onReviewAndSave?: () => void; 
}

export function SleepDialogFooter({
  currentStep,
  steps,
  saving,
  form,
  onBack,
  onNext,
  onReviewAndSave
}: SleepDialogFooterProps) {
  const currentStepIndex = steps.findIndex(step => step === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;
  const formValues = form.getValues();
  const isValid = isCurrentStepValid(currentStep, formValues);
  
  // Handle next or Review & Save button click
  const handleNextOrSubmit = () => {
    if (!isValid) return;
    
    // If it's not the last step, just go to the next step
    if (!isLastStep) {
      onNext();
      return;
    }
    
    // If it's the last step (notes), trigger the review and save process
    console.log('📝 [SleepDialogFooter.tsx:38] Review & Save clicked');
    
    if (onReviewAndSave) {
      onReviewAndSave();
    }
  };
  
  return (
    <div className="sticky bottom-0 border-t border-[#333333]/80 bg-[#1A1F2C]/95 backdrop-blur supports-[backdrop-filter]:bg-[#1A1F2C]/75 p-4">
      {/* Progress Dots */}
      <div className="flex justify-center mb-4">
        <div className="flex gap-1.5">
          {steps.map((step, index) => (
            <div
              key={step}
              className={`h-1.5 w-6 rounded-full transition-colors duration-200 ${
                currentStepIndex === index ? 'bg-[#9b87f5]' : 'bg-[#333333]'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-2">
        {/* Back Button */}
        <Button
          type="button"
          variant="outline"
          disabled={currentStepIndex === 0 || saving}
          onClick={onBack}
          className="border-[#333333] bg-[#222222] hover:bg-[#333333] hover:text-[#D6BCFA] disabled:bg-[#222222]/50 disabled:text-[#8E9196]/50"
        >
          Back
        </Button>
        
        {/* Next/Submit Button */}
        <Button
          type="button"
          disabled={!isValid || saving}
          onClick={handleNextOrSubmit}
          className="bg-[#9b87f5] hover:bg-[#9b87f5]/90 text-white disabled:bg-[#9b87f5]/50"
        >
          {isLastStep ? (saving ? "Saving..." : "Review & Save") : "Next"}
        </Button>
      </div>
      
      {/* Validation Message */}
      {!isValid && (
        <p className="text-[#f87171] text-sm mt-2 text-center">
          {getValidationMessage(currentStep)}
        </p>
      )}
    </div>
  );
}
